import { useState, useEffect, useRef } from "react";
import {
    Card,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@component/card";
import {
    CheckIcon,
    ChevronLeftIcon,
    Loader2Icon,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { ProgressBar, ProgressStep } from "@component/progress-bar";
import { DailyProvider, useCallObject } from "@daily-co/daily-react";
import { DailyCall } from "@daily-co/daily-js";
import { VideoInterviewRef, CustomInterviewSection } from "@type/videoInterview";
import { catchErrorLog } from "@util/sentry";
import { SpeechDetectionProvider } from "@context/speech_detection_context";
import BasicLayout from "@container/layout/basic";
import CustomVoiceInterview from "@container/custom-voice-interview";
import Snackbar from "@component/snackbar";
import { InterviewAssessment } from "@type/assessments";
import { demoInterviewInsight, getAssessment, getAssessmentCompanyDemo, triggerDemoNotification } from "@service/assessment";
import { getCookie, removeCookie } from "@util/cookie";
import { DemoCompanyInfo } from "@type/company";

// Define interface for form data
interface UserFormData {
    email: string;
    yourName: string;
}

export default function StartAIInterviewDemo() {
    const navigate = useNavigate();
    const { companyname } = useParams()
    const [interviewStage, setInterviewStage] = useState<string>("interview");
    const [isLoading, setIsLoading] = useState(true);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [userData, setUserData] = useState<UserFormData | null>(localStorage.getItem(`demoCompanyName:${companyname}`) ? JSON.parse(localStorage.getItem(`demoCompanyName:${companyname}`) || "{}") : null);
    const [userId, setUserId] = useState<string>("");
    const [interviewStatus, setInterviewStatus] = useState<string>("");
    const videoInterviewRef = useRef<VideoInterviewRef>(null);
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
    const [hideBackButton, setHideBackButton] = useState<boolean>(false);

    // New state for assessment data
    const [assessment, setAssessment] = useState<InterviewAssessment | null>(null);
    const [formattedSections, setFormattedSections] = useState<CustomInterviewSection[]>([]);
    const [isLoadingAssessment, setIsLoadingAssessment] = useState(true);
    const [isShowMessageRedirect, setIsShowMessageRedirect] = useState(false);
    const [assessmentError, setAssessmentError] = useState<string | null>(null);

    const [companyInfo, setCompanyInfo] = useState<DemoCompanyInfo | undefined>(undefined);
    const [assessmentId, setAssessmentId] = useState<string>("");

    useEffect(() => {
        if (companyname) {
            getAssessmentCompanyDemo(companyname).then((resp) => {
                if (resp.status === 200) {
                    setCompanyInfo(resp.data.data);
                    // Use the assessment_id from company info if available, otherwise use default
                    const finalAssessmentId = resp.data.data.assessment_id || import.meta.env.VITE_ASSESSMENT_DEMO_AI_ID;
                    setAssessmentId(finalAssessmentId);
                }
            })
        }
    }, [companyname])

    // Environment variables
    const API_BASE_URL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
    const PIPECAT_API_URL = import.meta.env.VITE_PIPECAT_API_URL;
    const PIPECAT_AGENT_NAME = import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME;
    const PIPECAT_AUTH_TOKEN = import.meta.env.VITE_PIPECAT_AUTH_TOKEN;
    const AWS_ASSUME_ROLE_ARN = import.meta.env.VITE_AWS_ASSUME_ROLE_ARN;
    const RECORDING_S3_BUCKET_REGION = import.meta.env.VITE_AWS_S3_BUCKET_REGION;
    const RECORDING_S3_BUCKET_NAME = import.meta.env.VITE_AWS_S3_BUCKET_NAME;

    // Fetch user data from localStorage and interview data from API on component mount
    useEffect(() => {
        // Only fetch assessment if assessmentId is set
        if (!assessmentId) {
            return;
        }

        setIsLoading(true);
        setIsLoadingAssessment(true);

        setInterviewStatus("Pending Interview");

        // Fetch assessment data using assessment ID from company info or default
        const fetchAssessmentData = async () => {
            try {
                setAssessmentError(null);
                console.log("Fetching demo ai interview assessment data...");
                console.log("Using demo ai Interview ID:", assessmentId);
                const response = await getAssessment(assessmentId);
                if (response) {
                    console.log("demo ai Assessment response:", response);
                    const assessmentData = response as InterviewAssessment;
                    setAssessment(assessmentData);

                    // Format sections from the assessment
                    if (assessmentData?.content?.sections) {
                        const sections = assessmentData.content.sections.map((section: any) => ({
                            title: section.title,
                            instruction: section.instruction,
                            duration: section.duration,
                            questions: section.questions?.map((q: any) => q.text).filter((text: string) => text?.trim() !== "") || undefined,
                            transition: section.transition || undefined
                        }));
                        console.log("Formatted demo ai Sections:", sections);
                        setFormattedSections(sections as CustomInterviewSection[]);
                    } else {
                        // Fallback to hardcoded sections if needed
                        console.log("Using default demo ai sections (assessment didn't contain sections)");
                    }
                } else {
                    console.error("Failed to fetch demo ai assessment, using default sections");
                    setAssessmentError("Failed to load screening data.");
                }
            } catch (err) {
                catchErrorLog(`Error fetching demo ai assessment data: ${err}`);
                setAssessmentError("An unexpected error occurred loading interview data.");
            } finally {
                setIsLoadingAssessment(false);
                setIsLoading(false);
            }
        };

        fetchAssessmentData();
    }, [assessmentId]);

    useEffect(() => {
        if (interviewStage === "complete" || interviewStatus === 'Complete') {
            setTimeout(() => {
                setIsShowMessageRedirect(true);
            }, 1000); // Redirect after 1 second
        }

    }, [interviewStage, interviewStatus]);

    // Check if we need Indonesian translation
    const isIndonesian = companyname === "kopi-kenangan";

    // Translation labels
    const labels = {
        back: isIndonesian ? "Kembali" : "Back",
        loading: isIndonesian ? "Memuat..." : "Loading...",
        completeTitle: isIndonesian ? "Wawancara Selesai!" : "Demo Interview Complete!",
        completeDescription: isIndonesian
            ? <>Terima kasih telah menyelesaikan wawancara demo dan <br /> harap tunggu untuk mendapatkan laporan wawancara!</>
            : <>Great work finishing your Personal Pitch demo interview and <br /> please stay tuned for the insights report!</>,
        snackbarMessage: isIndonesian
            ? "Wawancara berhasil diselesaikan!"
            : "Interview completed successfully!",
    };

    const steps: ProgressStep[] = [
        {
            label: isIndonesian ? "Wawancara AI" : "AI Interview",
            isCompleted: currentStep > 1,
            isActive: currentStep === 1,
        },
        {
            label: isIndonesian ? "Selesai" : "Complete",
            isCompleted: currentStep > 2,
            isActive: currentStep === 2,
        },
    ];

    // Get candidate name for conversation name
    const getCandidateName = () => {
        if (!userData) return "Anonymous Founder";
        return userData.email || "Anonymous Founder";
    };

    // Format user data for the AI context
    const formatUserData = (data: UserFormData | null) => {
        if (!data) return "";

        return `
Name: ${data.yourName}`.trim();
    };

    // Handle completion of interview and cleanup
    const handleInterviewComplete = () => {
        const conversationId = getCookie("interview_conversation_id");
        if (!conversationId) {
            alert("Error: Missing conversation ID. Please contact support.");
            catchErrorLog("Missing conversation ID in cookie during demo interview completion");
            return;
        }

        demoInterviewInsight(conversationId as string).then((resp) => {
            if (resp.status === 200) {
                sessionStorage.setItem("demo_interview_insights" + conversationId, JSON.stringify(resp.data.data.insights));
                if (videoInterviewRef.current) {
                    console.log("Ending interview and cleaning up media resources");
                    videoInterviewRef.current.endInterview('interview_completed');
                }
                setIsSnackbarOpen(true);
                setInterviewStage("complete");

                triggerDemoNotification({
                    company_name: companyInfo?.company_name || "",
                    user_name: userData?.yourName || "",
                    user_email: userData?.email || "",
                    alias: companyInfo?.alias || companyname || "",
                    conversation_id: conversationId
                }).then((resp) => {
                    if (resp.status === 200) {
                        if (videoInterviewRef.current) {
                            console.log("Ending interview and cleaning up media resources");
                            videoInterviewRef.current.endInterview('interview_completed');
                        }
                        setIsSnackbarOpen(true);
                        setInterviewStage("complete");
                        navigate(`/demo/${companyInfo?.alias || ""}/insight/${conversationId}`);
                    }
                }).catch((error) => {
                    catchErrorLog(`Error triggering demo ai notification: ${error}`);
                })
            }
        }).catch((error) => {
            if (error?.response?.status === 404) {
                setAssessmentError("It looks like the interview ended too early. We weren't able to generate your insight data. Please try again and complete the full interview.");
            } else {
                setAssessmentError("An unexpected error occurred. Please try again.");
            }
            catchErrorLog(`Error fetching demo interview insight: ${error}`);
        });
    };

    // Clean up any media when showing the completed screen directly due to status
    useEffect(() => {
        if (interviewStatus === 'Complete' && interviewStage === "interview") {
            console.log("Interview already completed - cleaning up resources");
            setInterviewStage("complete");
            setCurrentStep(2);
        }
    }, [interviewStatus, interviewStage]);

    const handleSnackbarClose = () => {
        setIsSnackbarOpen(false);
        // Remove the auth token from cookies
        removeCookie("authToken");
    };

    const callObject = useCallObject({}) as DailyCall;

    if (isLoading && isLoadingAssessment) {
        return <div>{labels.loading}</div>;
    }

    // Get the interview configuration based on assessment data
    // const INTERVIEW_CONFIG = getInterviewConfig();

    // Version without layout (for embedding in other components)
    return (
        <>
            <BasicLayout
                customLogo={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                disableMinHeight
                hideNavbarMenu
                style={{
                    height: "calc(100vh - 57px)", // Adjust height to fit below the header
                }}
                showFooter={false}
            >
                <div className="flex flex-col relative sm:mt-0 mb-20">
                    <div className="flex flex-row items-center justify-between mt-11">
                        <div className="flex-1 pl-2 sm:pl-0">
                            {!hideBackButton && (
                                <button
                                    onClick={() => {
                                        navigate(`/demo/${companyInfo?.alias || ""}`);
                                    }}
                                    className=" hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-md p-2 -m-2 flex gap-1 items-center"
                                >
                                    <ChevronLeftIcon className="h-5 w-5" style={{ color: "#09090b" }} />
                                    {labels.back}
                                </button>
                            )}
                        </div>
                        <div className="flex justify-center">
                            {companyInfo?.company_logo_interview && (
                                <img
                                    src={companyInfo?.company_logo_interview || ""}
                                    alt={companyInfo?.company_name || ""}
                                    className="h-16 w-auto object-contain"
                                />
                            )}
                        </div>
                        <div className="flex-1"></div>
                    </div>
                    <div className="container max-w-6xl mx-auto px-4 sm:py-8 flex flex-col min-h-[310px] items-center justify-center sm:mt-0">
                        {interviewStage === "interview" && interviewStatus !== 'Complete' && !isLoading ? (
                            <DailyProvider callObject={callObject}>
                                <SpeechDetectionProvider
                                    volumeThreshold={30}
                                    silenceDelay={400}
                                    autoStart={true}
                                >
                                    <CustomVoiceInterview
                                        ref={videoInterviewRef}
                                        requireSignIn={false}
                                        interviewId={assessmentId}
                                        pipecatApiUrl={PIPECAT_API_URL}
                                        agentName={PIPECAT_AGENT_NAME}
                                        authToken={PIPECAT_AUTH_TOKEN}
                                        s3BucketName={RECORDING_S3_BUCKET_NAME}
                                        s3BucketRegion={RECORDING_S3_BUCKET_REGION}
                                        awsAssumeRoleArn={AWS_ASSUME_ROLE_ARN}
                                        interviewName={assessment?.content.interviewName}
                                        fastapiURL={import.meta.env.VITE_TALENT_AI_BE_BASE_URL || ""}
                                        onCloseModal={handleInterviewComplete}
                                        customGreeting={assessment?.content.customGreeting}
                                        userId={userId}
                                        userCV={formatUserData(userData)}
                                        onCompletedInterview={() => {
                                            console.log("demo ai interview completed");
                                            // handleInterviewComplete();
                                        }}
                                        userName={userData?.email || ""}
                                        sections={formattedSections}
                                        description={assessment?.content.description || ""}
                                        maxDuration={assessment?.content.maxDuration || 20}
                                        aiName={assessment?.content.aiName || ""}
                                        aiLogoSrc={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                                        language="Demo"
                                        interviewLanguage={assessment?.content.interviewLanguage || "en-US"}
                                        userEmail={userData?.email || ""}
                                        concludingStatements="Thank you for trying out the ourteam demo interview. The interview is now concluded. We hope you enjoyed the experience!"
                                        voice={assessment?.content.voice || "male"}
                                        interviewContext={assessment?.content.interviewContext || ""}
                                        closingStatement={assessment?.content.closingStatement || ""}
                                        interviewerPersona={assessment?.content.interviewerPersona || ""}
                                        toneAndCommunication={assessment?.content.toneAndCommunication || undefined}
                                        interviewFlowPrinciples={assessment?.content.interviewFlowPrinciples || undefined}
                                        informationToShare={assessment?.content.informationToShare || undefined}
                                        idleTimeoutMessage={assessment?.content.idleTimeoutMessage}
                                        isShowBrand={true}
                                        companyPreferences={{
                                            primary_color: companyInfo?.company_color || "#000000",
                                            company_logo: companyInfo?.company_logo_interview || "",
                                            ai_bot_logo: "",
                                        }}
                                        handleEndSession={() => setHideBackButton(true)}
                                    />
                                </SpeechDetectionProvider>
                            </DailyProvider>
                        ) : null}
                        {assessmentError && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center max-w-md">
                                <p className="text-sm text-red-600">{assessmentError}</p>
                            </div>
                        )}
                        {(interviewStage === "complete" || interviewStatus === 'Complete') && (
                            <>
                                <Card>
                                    <CardHeader>
                                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                                        </div>
                                        <CardTitle className="text-center text-2xl">
                                            {labels.completeTitle}
                                        </CardTitle>
                                        <CardDescription className="text-center">
                                            <p className="text-black">
                                                {labels.completeDescription}
                                            </p>
                                        </CardDescription>
                                        {!assessmentError && (
                                            <div className="flex items-center justify-center gap-2 mt-4 text-gray-500">
                                                <Loader2Icon className="h-4 w-4 animate-spin" />
                                                <span className="text-sm">{isIndonesian ? "Menyiapkan hasil wawancara..." : "Generating your interview insights..."}</span>
                                            </div>
                                        )}
                                    </CardHeader>
                                </Card>
                            </>
                        )}
                    </div>
                </div>
            </BasicLayout>

            {/* Progress Bar */}
            {interviewStage === "interview" && interviewStatus !== 'Complete' && (
                <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t p-4">
                    <div className="container max-w-5xl mx-auto">
                        <ProgressBar currentStep={currentStep} steps={steps} isShowBanding={true} companyPreferences={{
                            primary_color: companyInfo?.company_color || "#000000",
                            company_logo: companyInfo?.company_logo_interview || "",
                            ai_bot_logo: "",
                        }} />
                    </div>
                </div>
            )}

            {/* Success Snackbar */}
            <Snackbar
                message={labels.snackbarMessage}
                variant="success"
                isOpen={isSnackbarOpen}
                onClose={handleSnackbarClose}
                duration={3000}
            />
        </>
    );
}
